<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";
import Swal from "sweetalert2";
import Multiselect from "vue-multiselect";

export default {
    components: { Layout, PageHeader, Multiselect },
    data() {
        return {
            title: "Digitalizador 2.0 — Subir Ficha",
            items: [
                { text: "Digitalizador 2.0", href: "/web/digitalizador2/subir" },
                { text: "Subir Ficha", active: true },
            ],
            // contexto del form (siempre disponible para el siguiente archivo)
            laborantes: [],
            coordinadores: [],
            eventos: [],
            laboranteSeleccionado: null,
            eventoSeleccionado: null,
            cargandoContexto: false,
            // upload
            archivoSeleccionado: null,
            nombreArchivo: null,
            previewUrl: null,
            cargando: false,
            // cola de fichas enviadas — cada item es independiente
            cola: [],
            _colaNextId: 0,
        };
    },

    beforeDestroy() {
        this.cola.forEach(item => this._stopPolling(item));
    },

    computed: {
        responsablesDisponibles() {
            const labs   = this.laborantes.map(u => ({ ...u, _tipo: 'Laborante' }));
            const coords = this.coordinadores.map(u => ({ ...u, _tipo: u.rol }));
            return [...labs, ...coords];
        },
    },

    mounted() {
        this.cargandoContexto = true;
        Promise.all([
            axios.get('/api/get_laborantes'),
            axios.get('/api/get_usuarios'),
            axios.get('/api/get_eventos_salida'),
        ]).then(([labRes, userRes, evRes]) => {
            this.laborantes    = labRes.data.usuarios  || [];
            this.coordinadores = (userRes.data.usuarios || []).filter(u => u.rol === 'Coordinador');
            this.eventos       = evRes.data.eventos    || [];
        }).catch(() => {}).finally(() => {
            this.cargandoContexto = false;
        });
    },

    methods: {
        // ─── Selección de archivo ─────────────────────────────────────────
        onArchivoChange(e) {
            const file = e.target.files[0];
            if (!file) return;
            this.archivoSeleccionado = file;
            this.nombreArchivo = file.name;
            this.previewUrl = (file.type.startsWith("image/") || file.type === "application/pdf")
                ? URL.createObjectURL(file)
                : null;
        },

        limpiarFormulario() {
            this.archivoSeleccionado = null;
            this.nombreArchivo = null;
            this.previewUrl = null;
            if (this.$refs.inputArchivo) this.$refs.inputArchivo.value = "";
            // NO se limpia evento ni laborante — agiliza subir el siguiente archivo
        },

        // ─── Envío del archivo (no bloquea — form queda libre de inmediato) ────
        async enviarArchivo() {
            if (!this.archivoSeleccionado) {
                Swal.fire("Atención", "Debes seleccionar un archivo antes de subir.", "warning");
                return;
            }

            this.cargando = true;

            // Capturar contexto antes de limpiar
            const archivoNombre    = this.archivoSeleccionado.name;
            const eventoId         = this.eventoSeleccionado?.id         ?? null;
            const eventoNombre     = this.eventoSeleccionado
                ? `${this.eventoSeleccionado.codigo} — ${this.eventoSeleccionado.nombre_difusion}`
                : null;
            const laboranteId      = this.laboranteSeleccionado?.id      ?? null;
            const laboranteNombre  = this.laboranteSeleccionado?.name    ?? null;

            const formData = new FormData();
            formData.append("file", this.archivoSeleccionado);
            if (eventoId)   { formData.append("event_id",    String(eventoId)); }
            if (eventoNombre) { formData.append("event_name", this.eventoSeleccionado.nombre_difusion || ""); }

            try {
                const res = await axios.post("/api/digitalizador2/upload", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                const data = res.data;
                if (!data.request_id) {
                    Swal.fire("Error", data.message || "No se pudo iniciar el procesamiento.", "error");
                    return;
                }

                // Agregar a la cola y limpiar el selector de archivo de inmediato
                const item = this._crearItemCola({
                    requestId:      data.request_id,
                    estado:         data.status || "pending",
                    archivoNombre,
                    eventoId,
                    eventoNombre,
                    laboranteId,
                    laboranteNombre,
                });
                this.cola.unshift(item);
                this.limpiarFormulario();

                // Polling en background (no bloquea UI)
                if (!["completed", "failed", "needs_review"].includes(item.estado)) {
                    this._startPolling(item);
                } else if (item.estado !== "failed") {
                    this._cargarResultado(item);
                }

            } catch (err) {
                const msg = err.response?.data?.message || err.response?.data?.error || "Error al subir el archivo.";
                Swal.fire("Error", msg, "error");
            } finally {
                this.cargando = false;
            }
        },

        // ─── Cola: helpers ────────────────────────────────────────────────
        _crearItemCola({ requestId, estado, archivoNombre, eventoId, eventoNombre, laboranteId, laboranteNombre }) {
            return {
                _id:            ++this._colaNextId,
                requestId,
                estado,
                resultado:      null,
                archivoNombre,
                eventoId,
                eventoNombre,
                laboranteId,
                laboranteNombre,
                timestamp:      new Date().toLocaleTimeString('es-CL'),
                // revisión inline
                campoEditando:  null,
                valorEditado:   "",
                // CRM
                enviandoCrm:    false,
                estadoCrm:      null,
                mensajeCrm:     null,
                // UI
                expandido:      true,
                _polling:       null,
            };
        },

        eliminarDeQueue(item) {
            this._stopPolling(item);
            const idx = this.cola.indexOf(item);
            if (idx !== -1) this.cola.splice(idx, 1);
        },

        // ─── Polling por item (background) ───────────────────────────────
        _startPolling(item) {
            this._stopPolling(item);
            item._polling = setInterval(() => this._pollItem(item), 3000);
        },

        _stopPolling(item) {
            if (item._polling) {
                clearInterval(item._polling);
                item._polling = null;
            }
        },

        async _pollItem(item) {
            try {
                const res = await axios.get(`/api/digitalizador2/status/${item.requestId}`);
                item.estado = res.data.status;
                if (["completed", "failed", "needs_review"].includes(item.estado)) {
                    this._stopPolling(item);
                    if (item.estado !== "failed") {
                        await this._cargarResultado(item);
                    }
                }
            } catch (_) {}
        },

        async _cargarResultado(item) {
            try {
                const res = await axios.get(`/api/digitalizador2/result/${item.requestId}`);
                item.resultado = res.data;
            } catch (err) {
                console.error("Error cargando resultado:", err);
            }
        },

        // ─── Revisión de campos (por item) ───────────────────────────────
        iniciarEdicion(item, index, valorActual) {
            item.campoEditando = index;
            item.valorEditado  = valorActual || "";
        },

        cancelarEdicion(item) {
            item.campoEditando = null;
            item.valorEditado  = "";
        },

        async guardarCorreccion(item, index) {
            try {
                await axios.post(`/api/digitalizador2/correct/${item.requestId}/${index}`, {
                    corrected_value: item.valorEditado,
                });
                await this._cargarResultado(item);
                item.campoEditando = null;
                item.valorEditado  = "";
            } catch (err) {
                Swal.fire("Error", "No se pudo guardar la corrección.", "error");
            }
        },

        async aprobarCampo(item, index) {
            try {
                await axios.post(`/api/digitalizador2/approve/${item.requestId}/${index}`);
                await this._cargarResultado(item);
            } catch (err) {
                Swal.fire("Error", "No se pudo aprobar el campo.", "error");
            }
        },

        // ─── CRM por item ─────────────────────────────────────────────────
        _itemsResultado(item) {
            return item.resultado?.items || [];
        },

        _camposPendientes(item) {
            return this._itemsResultado(item).filter(f => !['approved', 'corrected'].includes(f.status));
        },

        _todosRevisados(item) {
            const todos = this._itemsResultado(item);
            return todos.length > 0 && this._camposPendientes(item).length === 0;
        },

        _puedeEnviarAlCrm(item) {
            return (
                this._todosRevisados(item) &&
                ['completed', 'needs_review'].includes(item.estado) &&
                item.estadoCrm !== 'enviado'
            );
        },

        async enviarAlCrm(item) {
            if (!this._puedeEnviarAlCrm(item)) return;

            const confirm = await Swal.fire({
                title: "¿Enviar al CRM?",
                text: "Se registrará la inscripción en el CRM de UDLA. Esta acción no se puede deshacer.",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Sí, enviar",
                cancelButtonText: "Cancelar",
            });
            if (!confirm.isConfirmed) return;

            item.enviandoCrm = true;
            item.mensajeCrm  = null;
            try {
                const res = await axios.post(`/api/digitalizador2/send-crm/${item.requestId}`, {
                    event_id:     item.eventoId    ?? null,
                    laborante_id: item.laboranteId ?? null,
                });
                const crm = res.data.crm_data || {};
                if (crm.status === '0' || crm.status === 0) {
                    item.estadoCrm = "enviado";
                    item.mensajeCrm = "Inscripción enviada correctamente al CRM.";
                } else if (crm.message && crm.message.toLowerCase().includes("ya se encuentra registrado")) {
                    item.estadoCrm = "enviado";
                    item.mensajeCrm = "El alumno ya estaba registrado en este evento (duplicado).";
                } else {
                    item.estadoCrm = "error";
                    item.mensajeCrm = crm.message || "Respuesta inesperada del CRM.";
                }
            } catch (err) {
                item.estadoCrm = "error";
                item.mensajeCrm = err.response?.data?.error || "No se pudo enviar al CRM.";
                Swal.fire("Error CRM", item.mensajeCrm, "error");
            } finally {
                item.enviandoCrm = false;
            }
        },

        // ─── Helpers UI ───────────────────────────────────────────────────
        estadoConfig(estado) {
            const map = {
                pending:      { label: "En cola",           color: "warning", spin: false },
                queued:       { label: "En cola",           color: "warning", spin: false },
                processing:   { label: "Procesando…",       color: "info",    spin: true  },
                completed:    { label: "Completado",        color: "success", spin: false },
                failed:       { label: "Error",             color: "danger",  spin: false },
                needs_review: { label: "Requiere revisión", color: "warning", spin: false },
            };
            return map[estado] || { label: estado, color: "secondary", spin: false };
        },

        confianzaColor(conf) {
            if (conf == null) return "secondary";
            if (conf >= 0.85) return "success";
            if (conf >= 0.6)  return "warning";
            return "danger";
        },

        confianzaPct(conf) {
            if (conf == null) return "—";
            return Math.round(conf * 100) + "%";
        },

        abrirEnNuevaPestana(item) {
            window.open(`https://apidocs.alegoria.cl/demo/results/${item.requestId}`, "_blank");
        },

        labelResponsable(u) {
            return `${u.name} (${u._tipo || u.rol})`;
        },

        labelEvento(e) {
            return `${e.codigo} — ${e.nombre_difusion}`;
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="row">
            <!-- ══════════════════════════════════════════════════════════════
                 Panel izquierdo: formulario siempre disponible
                 El form se limpia sólo el selector de archivo tras subir;
                 el evento y responsable se mantienen para agilizar el siguiente.
            ══════════════════════════════════════════════════════════════ -->
            <div class="col-xl-4">
                <div class="card">
                    <div class="card-body">
                        <h5 class="card-title mb-3">
                            <i class="bx bx-upload mr-1"></i> Subir ficha para digitalizar
                        </h5>

                        <!-- Selector de archivo -->
                        <div class="form-group">
                            <label>Archivo (PDF, JPG, PNG)</label>
                            <div class="custom-file">
                                <input
                                    ref="inputArchivo"
                                    type="file"
                                    class="custom-file-input"
                                    id="inputFicha"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    @change="onArchivoChange"
                                />
                                <label class="custom-file-label" for="inputFicha">
                                    {{ nombreArchivo || "Seleccionar archivo…" }}
                                </label>
                            </div>
                        </div>

                        <!-- Responsable (laborante o coordinador) -->
                        <div class="form-group">
                            <label>Responsable <span class="text-muted small">(laborante o coordinador)</span></label>
                            <multiselect
                                v-model="laboranteSeleccionado"
                                :options="responsablesDisponibles"
                                :custom-label="labelResponsable"
                                placeholder="Seleccionar responsable…"
                                selectLabel="Seleccionar"
                                selectedLabel="Seleccionado"
                                deselectLabel="Remover"
                                :loading="cargandoContexto"
                                track-by="id"
                            >
                                <template slot="noOptions">Sin responsables disponibles</template>
                                <template slot="noResult">Sin coincidencias</template>
                            </multiselect>
                        </div>

                        <!-- Evento asociado -->
                        <div class="form-group">
                            <label>Evento asociado <span class="text-danger">*</span></label>
                            <multiselect
                                v-model="eventoSeleccionado"
                                :options="eventos"
                                :custom-label="labelEvento"
                                placeholder="Seleccionar evento…"
                                selectLabel="Seleccionar"
                                selectedLabel="Seleccionado"
                                deselectLabel="Remover"
                                :loading="cargandoContexto"
                                track-by="id"
                            >
                                <template slot="noOptions">Sin eventos disponibles</template>
                                <template slot="noResult">Sin coincidencias</template>
                            </multiselect>
                        </div>

                        <!-- Acciones -->
                        <div class="d-flex align-items-center flex-wrap">
                            <button
                                class="btn btn-primary mr-2 mb-1"
                                :disabled="!archivoSeleccionado || !eventoSeleccionado || cargando"
                                @click="enviarArchivo"
                            >
                                <span v-if="cargando">
                                    <i class="bx bx-loader bx-spin mr-1"></i> Subiendo…
                                </span>
                                <span v-else>
                                    <i class="bx bx-cloud-upload mr-1"></i> Digitalizar
                                </span>
                            </button>
                            <button
                                v-if="archivoSeleccionado"
                                class="btn btn-outline-secondary btn-sm mb-1"
                                @click="limpiarFormulario"
                            >
                                <i class="bx bx-x mr-1"></i> Limpiar
                            </button>
                        </div>
                        <small v-if="archivoSeleccionado && !eventoSeleccionado" class="text-danger d-block mt-1">
                            <i class="bx bx-error-circle"></i> Selecciona un evento para continuar.
                        </small>
                        <small v-if="cola.length > 0" class="text-muted d-block mt-2">
                            <i class="bx bx-info-circle mr-1"></i>
                            El OCR se procesa en segundo plano. Puedes subir la siguiente ficha sin esperar.
                        </small>
                    </div>
                </div>

                <!-- Preview del archivo seleccionado -->
                <div v-if="previewUrl" class="card">
                    <div class="card-body p-2">
                        <h6 class="card-title mb-2">
                            <i class="bx bx-image mr-1"></i> Vista previa
                        </h6>
                        <img
                            v-if="archivoSeleccionado && archivoSeleccionado.type.startsWith('image/')"
                            :src="previewUrl"
                            class="img-fluid rounded"
                            style="max-height: 400px; width: 100%; object-fit: contain;"
                        />
                        <iframe
                            v-if="archivoSeleccionado && archivoSeleccionado.type === 'application/pdf'"
                            :src="previewUrl"
                            class="w-100 rounded border"
                            style="height: 400px;"
                        ></iframe>
                    </div>
                </div>
            </div>

            <!-- ══════════════════════════════════════════════════════════════
                 Panel derecho: cola de fichas enviadas
                 Cada item tiene su propio estado/polling/resultado/CRM.
                 El usuario puede enviar múltiples fichas sin esperar el OCR.
            ══════════════════════════════════════════════════════════════ -->
            <div class="col-xl-8">
                <!-- Vacío -->
                <div v-if="cola.length === 0" class="card border-dashed">
                    <div class="card-body text-center py-5 text-muted">
                        <i class="bx bx-cloud-upload display-4 mb-2"></i>
                        <p class="mb-0">No hay fichas enviadas todavía.</p>
                        <p class="small">Sube una ficha desde el panel de la izquierda. El OCR se procesará en segundo plano.</p>
                    </div>
                </div>

                <!-- Items de la cola -->
                <div
                    v-for="item in cola"
                    :key="item._id"
                    class="card mb-3"
                    :class="{
                        'border-success': item.estado === 'completed',
                        'border-danger':  item.estado === 'failed',
                        'border-warning': item.estado === 'needs_review',
                    }"
                >
                    <!-- Cabecera del item -->
                    <div
                        class="card-header d-flex align-items-center py-2 cursor-pointer"
                        @click="item.expandido = !item.expandido"
                        style="cursor: pointer;"
                    >
                        <i
                            :class="[
                                'bx mr-2',
                                estadoConfig(item.estado).spin ? 'bx-loader bx-spin text-info' :
                                item.estado === 'completed'    ? 'bx-check-circle text-success' :
                                item.estado === 'failed'       ? 'bx-x-circle text-danger'      :
                                item.estado === 'needs_review' ? 'bx-error-circle text-warning' :
                                'bx-time-five text-secondary'
                            ]"
                        ></i>
                        <span class="font-weight-bold text-truncate mr-2" style="max-width: 220px;" :title="item.archivoNombre">
                            {{ item.archivoNombre }}
                        </span>
                        <small v-if="item.eventoNombre" class="text-muted text-truncate mr-auto" style="max-width: 200px;" :title="item.eventoNombre">
                            {{ item.eventoNombre }}
                        </small>
                        <span v-else class="mr-auto"></span>

                        <span :class="`badge badge-${estadoConfig(item.estado).color} mr-2 flex-shrink-0`">
                            <i v-if="estadoConfig(item.estado).spin" class="bx bx-loader bx-spin mr-1"></i>
                            {{ estadoConfig(item.estado).label }}
                        </span>
                        <small class="text-muted mr-2 flex-shrink-0">{{ item.timestamp }}</small>
                        <i :class="item.expandido ? 'bx bx-chevron-up text-muted' : 'bx bx-chevron-down text-muted'" class="flex-shrink-0 mr-1"></i>
                        <button
                            v-if="['completed', 'failed', 'needs_review'].includes(item.estado)"
                            class="btn btn-link btn-sm p-0 text-danger flex-shrink-0"
                            title="Quitar de la lista"
                            @click.stop="eliminarDeQueue(item)"
                        >
                            <i class="bx bx-trash"></i>
                        </button>
                    </div>

                    <!-- Cuerpo del item -->
                    <div v-if="item.expandido" class="card-body pt-2 pb-3">

                        <!-- Procesando en segundo plano -->
                        <div v-if="['pending', 'queued', 'processing'].includes(item.estado)" class="text-center py-3 text-muted">
                            <i class="bx bx-loader bx-spin mr-1 text-primary"></i>
                            El OCR está procesando en segundo plano…
                            <div class="mt-1"><small>ID: {{ item.requestId }}</small></div>
                            <small class="d-block mt-1">Puedes subir otra ficha mientras tanto.</small>
                        </div>

                        <!-- Error -->
                        <div v-else-if="item.estado === 'failed'" class="text-center py-3">
                            <i class="bx bx-x-circle display-4 text-danger"></i>
                            <p class="mt-2 text-danger font-weight-bold mb-1">El procesamiento del documento falló.</p>
                            <small class="text-muted">ID: {{ item.requestId }}</small>
                        </div>

                        <!-- Resultado disponible -->
                        <div v-else-if="item.resultado">
                            <!-- Contexto y acciones rápidas -->
                            <div class="d-flex flex-wrap align-items-center mb-2 pb-2 border-bottom">
                                <div v-if="item.laboranteNombre" class="mr-3 small text-muted">
                                    <i class="bx bx-user mr-1"></i>{{ item.laboranteNombre }}
                                </div>
                                <div class="ml-auto d-flex flex-wrap">
                                    <button class="btn btn-outline-info btn-sm mr-1 mb-1" @click="abrirEnNuevaPestana(item)">
                                        <i class="bx bx-link-external mr-1"></i> Demo
                                    </button>
                                </div>
                            </div>

                            <!-- Barra de progreso de revisión -->
                            <div v-if="_itemsResultado(item).length" class="mb-3">
                                <div class="d-flex justify-content-between small mb-1">
                                    <span class="font-weight-bold">Revisión de campos</span>
                                    <span>
                                        {{ _itemsResultado(item).length - _camposPendientes(item).length }}
                                        / {{ _itemsResultado(item).length }} aprobados
                                    </span>
                                </div>
                                <div class="progress" style="height: 6px;">
                                    <div
                                        class="progress-bar"
                                        :class="_todosRevisados(item) ? 'bg-success' : 'bg-warning'"
                                        :style="`width: ${_itemsResultado(item).length ? Math.round(((_itemsResultado(item).length - _camposPendientes(item).length) / _itemsResultado(item).length) * 100) : 0}%`"
                                    ></div>
                                </div>
                                <small v-if="!_todosRevisados(item)" class="text-muted">
                                    Faltan {{ _camposPendientes(item).length }} campo(s) por revisar antes de poder enviar al CRM.
                                </small>
                            </div>

                            <!-- Tabla de campos -->
                            <div v-if="_itemsResultado(item).length" class="table-responsive mb-3">
                                <table class="table table-sm table-hover mb-0">
                                    <thead class="thead-light">
                                        <tr>
                                            <th style="width: 28%">Campo</th>
                                            <th style="width: 37%">Valor extraído</th>
                                            <th style="width: 15%" class="text-center">Confianza</th>
                                            <th style="width: 20%" class="text-center">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(campo, idx) in _itemsResultado(item)"
                                            :key="idx"
                                            :class="{
                                                'table-success': campo.status === 'approved' || campo.status === 'corrected',
                                                'table-warning': campo.status !== 'approved' && campo.status !== 'corrected' && campo.confidence < 0.6
                                            }"
                                        >
                                            <td class="font-weight-bold small align-middle">{{ campo.label || campo.field }}</td>

                                            <td class="align-middle">
                                                <div v-if="item.campoEditando === idx" class="d-flex">
                                                    <input
                                                        v-model="item.valorEditado"
                                                        type="text"
                                                        class="form-control form-control-sm mr-1"
                                                        @keyup.enter="guardarCorreccion(item, idx)"
                                                        @keyup.esc="cancelarEdicion(item)"
                                                    />
                                                    <button class="btn btn-success btn-sm mr-1" @click="guardarCorreccion(item, idx)">
                                                        <i class="bx bx-check"></i>
                                                    </button>
                                                    <button class="btn btn-secondary btn-sm" @click="cancelarEdicion(item)">
                                                        <i class="bx bx-x"></i>
                                                    </button>
                                                </div>
                                                <span v-else>
                                                    {{ campo.corrected_value || campo.value || '—' }}
                                                    <span v-if="campo.corrected_value && campo.value" class="ml-1 text-muted small">
                                                        <del>{{ campo.value }}</del>
                                                    </span>
                                                </span>
                                            </td>

                                            <td class="text-center align-middle">
                                                <span :class="`badge badge-${confianzaColor(campo.confidence)}`">
                                                    {{ confianzaPct(campo.confidence) }}
                                                </span>
                                            </td>

                                            <td class="text-center align-middle">
                                                <span v-if="campo.status === 'approved' || campo.status === 'corrected'" class="text-success small">
                                                    <i class="bx bx-check-double"></i> OK
                                                </span>
                                                <div v-else class="d-flex justify-content-center">
                                                    <button
                                                        v-if="item.campoEditando !== idx"
                                                        class="btn btn-outline-primary btn-xs mr-1"
                                                        title="Editar"
                                                        @click="iniciarEdicion(item, idx, campo.corrected_value || campo.value)"
                                                    >
                                                        <i class="bx bx-edit-alt"></i>
                                                    </button>
                                                    <button
                                                        class="btn btn-outline-success btn-xs"
                                                        title="Aprobar"
                                                        @click="aprobarCampo(item, idx)"
                                                    >
                                                        <i class="bx bx-check"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div v-else class="text-center text-muted py-2 small">
                                No se encontraron campos en el resultado.
                            </div>

                            <!-- Panel CRM -->
                            <div
                                class="rounded p-3 mt-1"
                                :class="{
                                    'bg-success-subtle border border-success': item.estadoCrm === 'enviado',
                                    'bg-danger-subtle  border border-danger':  item.estadoCrm === 'error',
                                    'bg-light          border':                item.estadoCrm === null,
                                }"
                                style="background:#f8f9fa;"
                            >
                                <div v-if="item.estadoCrm === 'enviado'" class="text-success small">
                                    <i class="bx bx-check-circle mr-1"></i> {{ item.mensajeCrm }}
                                </div>
                                <div v-else-if="item.estadoCrm === 'error'" class="text-danger small">
                                    <i class="bx bx-error-circle mr-1"></i> {{ item.mensajeCrm }}
                                </div>
                                <div v-else class="d-flex align-items-center flex-wrap">
                                    <button
                                        class="btn btn-primary btn-sm mr-2"
                                        :disabled="!_puedeEnviarAlCrm(item) || item.enviandoCrm"
                                        @click="enviarAlCrm(item)"
                                    >
                                        <span v-if="item.enviandoCrm">
                                            <i class="bx bx-loader bx-spin mr-1"></i> Enviando…
                                        </span>
                                        <span v-else-if="_todosRevisados(item)">
                                            <i class="bx bx-send mr-1"></i> Enviar al CRM
                                        </span>
                                        <span v-else>
                                            <i class="bx bx-lock-alt mr-1"></i> Revisar campos primero
                                        </span>
                                    </button>
                                    <small v-if="item.eventoNombre" class="text-muted">
                                        <i class="bx bx-calendar-event mr-1"></i>{{ item.eventoNombre }}
                                    </small>
                                </div>
                            </div>
                        </div>

                        <!-- Sin resultado todavía pero estado final (raro) -->
                        <div v-else-if="['completed', 'needs_review'].includes(item.estado)" class="text-center text-muted py-2 small">
                            <i class="bx bx-loader bx-spin mr-1"></i> Cargando resultado…
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
.border-dashed {
    border: 2px dashed #dee2e6 !important;
}
.btn-xs {
    padding: 0.15rem 0.4rem;
    font-size: 0.75rem;
    line-height: 1.2;
}
</style>
